/* jshint esversion: 6, -W097 */
/* globals module */
'use strict';

/**
 * @module enervee_utils.cms
 */
CmsContentService.$inject = [
    '$http',
    '$log',
    '$q',
    'StorageService',
    'TemplateService',
    'UtilService',
];
function CmsContentService(
    $http,
    $log,
    $q,
    StorageService,
    TemplateService,
    UtilService
) {
    var _pub = {};
    var dataPath = '_cmsContent.texts';
    var promisesPath = '_cmsContent.promises';

    /**
     * Returns a promise that resolves with a specific retrieved
     * marketplace text value based on the given key.
     * If the content for the root key of the group (i.e. 'index_page'
     * in 'index_page:main_content') has not been requested yet, it is
     * requested now.
     *
     * If a context object is passed, each instance of `{key}` in the
     * retrieved CMS content will be replaced with the value of that
     * `key` in the context object.
     *
     * @example
     * // Requesting text with only a key
     * CmsContentService.getMarketPlaceText('index_page:main_content').then(function(text) {
     *   vm.doSomethingWith = text;
     * });
     * @example
     * // Requesting text with a key and a category id
     * CmsContentService.getMarketPlaceText('index_page:main_content', 94).then(function(text) {
     *   vm.doSomethingWith = text;
     * });
     * @example
     * // Requesting text with a key and simple context to be rendered
     * CmsContentService.getMarketPlaceText(
     *   'index_page:main_content',
     *   {
     *     'context_key': 'Context Value',
     *   }
     * ).then(function(text) {
     *   vm.doSomethingWith = text;
     * });
     *
     * @param {String} key a ":"-separated key specifying the text to get
     * @param {Object} [context] A hash of "variables" to render in the CMS text. See
     * `renderContext` for more advanced use cases.
     * @param {String|Number} [categoryId]
     * @returns {Promise} Resolves to a CMS text with the context rendered.
     */
    _pub.getMarketPlaceText = function (key, context, categoryId) {
        if (typeof categoryId === 'undefined') {
            categoryId = 'default';
        }
        var baseKey = key.split(':')[0];
        var path = key.split(':').concat([categoryId]);

        if (angular.isUndefined(getText(path))) {
            return fetchCMSText(baseKey).then(function () {
                var text = getText(path) || "";
                if (!text) {
                    $log.warn("The given marketplace text key " + path.slice(0, -1).join(':') + " does not exist");
                }
                return renderContext(text, context);
            });
        }

        return $q.resolve(renderContext(getText(path), context));
    };

    /**
     * Retrieves the cms content for a specific marketplace text group
     *
     * @example
     * CmsContentService.retrieveCMSContent(['base']);
     * CmsContentService.retrieveCMSContent(['base', 'results_page']);
     *
     * @param {String|Array} cmsMarketPlaceGroups
     * @returns {Promise} Resolves when all the requested groups have been fetched and
     * stored in the data store.
     */
    _pub.retrieveCMSContent = function (cmsMarketPlaceGroups) {
        return fetchCMSText(cmsMarketPlaceGroups);
    };

    /**
     * Retrieves the CMS value based on the given sectionKey, and initializes it into the objectToPopulate.propertyName
     * If a context object is passed, the retrieved text is redered using the context object.
     *
     * @param {Object} objectToPopulate
     * @param {String} propertyName
     * @param {String} key a ":"-separated key specifying the text to get
     * @param {Object} [context] A hash of "variables" to render in the CMS text. See
     * `renderContext` for more advanced use cases.
     * @param {String|Number} [categoryId]
     */
    _pub.retrieveCMSValue = function (objectToPopulate, propertyName, key, context, categoryId) {
        if (!objectToPopulate) {
            $log.warn("The object to be populated by cms marketplace content has not been provided");
            return;
        }

        if (!propertyName) {
            $log.warn("The property of the object to be populated by cms marketplace content has not been provided");
            return;
        }

        if (!key) {
            $log.warn("The section key to retrieve from the retrieved CMS values has not been provided.");
            return;
        }

        return _pub.getMarketPlaceText(key, context, categoryId).then(function(text) {
            objectToPopulate[propertyName] = text;
        });
    };

    /**
     * Fetches marketplace text from the CMS system, setting the group
     * as being `requested` in order to avoid duplicate requests.
     *
     * @param {String|Array<String>} cmsGroups
     * @returns {Promise} Resolves when all the groups that need to be fetched have been
     * fetched and stored in the data store.
     */
    function fetchCMSText(cmsGroups) {
        if (!Array.isArray(cmsGroups)) {
            cmsGroups = [cmsGroups];
        }
        var requestedGroups = [];
        var unrequestedGroups = [];
        cmsGroups.forEach(function (group) {
            var groupPromise = getGroupPromise(group);
            if (typeof groupPromise !== 'undefined') {
                requestedGroups.push(groupPromise);
            } else {
                unrequestedGroups.push(group);
            }
        });
        if (unrequestedGroups.length === 0) {
            // If there are no new requests to make, wait for the current ones to finish
            return $q.all(requestedGroups);
        }

        var _httpPromise = requestCMSGroups(unrequestedGroups);
        unrequestedGroups.forEach(function (group) {
            setGroupPromise(group, _httpPromise);
        });

        return $q.all(requestedGroups.concat([_httpPromise]));
    }

    /**
     * Helper methods that sends the request to the CMS to get text for the specified
     * groups.
     *
     * @param {String|Array<String>} cmsGroups
     * @return {Promise} Resolves when all the requested groups have been fetched and
     * stored in the data store.
     */
    function requestCMSGroups(cmsGroups) {
        return $http({
            url: UtilService.getSiteSetting('cms_base_url') + 'marketplace-texts/',
            method: "GET",
            params: {'group': cmsGroups}
        }).then(function (response) {
            var data = response.data || {};
            Object.keys(data).forEach(function (group) {
                setGroupText(group, data[group]);
            });
        }, function (error) {
            $log.error("Error retrieving CMS groups: " + cmsGroups.join(', '), error);
        });
    }

    /**
     * Renders a chunk of marketplace text with a context object. Returns a promise because
     * of possibly nested contexts.
     * When the value for a context "variable" is an array, the first item should be a
     * string with its own "variables" to be rendered, and the second item should be the
     * hash of "variables" where the values are CMS keys.
     *
     * @example
     * // "Simple" context (string values only)
     * renderContext(
     *   "This is {place}! Did you think {place} would fall that easily?",
     *   {
     *     place: "Sparta"
     *   }
     * );
     * // Resolves to: "This is Sparta! Did you think Sparta would fall that easily?"
     * @example
     * // "Complex" context (strings or array values)
     * renderContext(
     *   "Please see our {faq_link} for more details.",
     *   {
     *     faq_link: [
     *       '<a href="/faq/">{link_text}</a>',
     *       {
     *         link_text: "base:faq_name"
     *       }
     *     ]
     *   }
     * );
     * // Assume "base:faq_name" resolves to "FAQ".
     * // Resolves to: "Please see our <a href="/faq/">FAQ</a> for more details."
     *
     * @param {String} text
     * @param {Object} [context] A hash of "variables" to render in the CMS text.
     * @returns {Promise} Resolves to the rendered text.
     */
    function renderContext(text, context) {
        var recursiveRenders = {};
        var simpleContext = {};
        if (context) {
            Object.keys(context).forEach(function(key) {
                var contextValue = context[key];

                if (Array.isArray(contextValue)) {
                    var contextInternalValueHtml = contextValue[0];
                    var contextInternalValueContexts = contextValue[1];
                    recursiveRenders[key] = deferredRender(contextInternalValueHtml, contextInternalValueContexts);
                } else {
                    simpleContext[key] = contextValue;
                }
            });
        }

        if (Object.keys(recursiveRenders).length > 0) {
            return $q.all(recursiveRenders).then(function(recursiveContext) {
                var newContext = Object.assign({}, simpleContext, recursiveContext);
                return renderContext(text, newContext).then(function(finalCmsContent) {
                    return finalCmsContent;
                });
            });
        }

        text = TemplateService.render(text, simpleContext);
        return $q.resolve(text);
    }

    /**
     * Deferred rendering of context, given a chunk of text and a
     * sectionKey, it retrieves the CMS content, then uses it to render
     * the context object. Assumes all the values in the context object
     * are sectionKeys for CMS content
     *
     * @example
     * deferredRender(
     *   '<a href="/faq/">{link_text}</a>',
     *   {
     *     link_text: "base:faq_name"
     *   }
     * );
     * // Assume "base:faq_name" resolves to "FAQ"
     * // Resolves to: "<a href="/faq/">FAQ</a>"
     *
     * @param {String} text
     * @param {Object} context A hash of "variables" to render in the text, where the
     * values are CMS keys to retrieve more text.
     * @returns {Promise} Resolves to the rendered text.
     */
    function deferredRender(text, context) {
        var cmsContextStrings = {};
        Object.keys(context).forEach(function(key) {
            // Stores a promise for each piece of marketplace text
            cmsContextStrings[key] = _pub.getMarketPlaceText(context[key]);
        });

        // When all the marketplace texts have been retrieved...
        return $q.all(cmsContextStrings).then(function(retrievedContext) {
            return TemplateService.render(text, retrievedContext);
        });
    }

    /**
     * Helper function to shortcut getting values in StorageService's `_cmsContent.texts`
     *
     * @param {String} key
     * @return {String}
     */
    function getText(key) {
        // Copy the `key` argument, cause we're gonna mutate it.
        key = key.slice(0);
        var categoryId = key.pop();
        var text = StorageService.get([dataPath, key]);
        if (angular.isUndefined(text)) return undefined;

        // Try to find the text for the specific category, or the default text for this key.
        text = text[categoryId] || text.default;
        // Hopefully we have a category specific or default text, but we might not.
        if (angular.isUndefined(text)) return undefined;
        if (text && !text.enabled) {
            $log.warn("The given marketplace text key " + key.join(':') + " is not enabled");
            return "";
        }
        return text.value;
    }

    /**
     * Helper function to shortcut setting values in StorageService's `_cmsContent.texts`
     *
     * @param {String} group
     * @param {Object} data
     * @return {Object}
     */
    function setGroupText(group, data) {
        return StorageService.set([dataPath, group], data);
    }

    /**
     * Helper function to shortcut getting values in StorageService's `_cmsContent.promises`
     *
     * @param {String} group
     * @return {Promise}
     */
    function getGroupPromise(group) {
        return StorageService.get([promisesPath, group]);
    }

    /**
     * Helper function to shortcut setting values in StorageService's `_cmsContent.promises`
     *
     * @param {String} group
     * @param {Promise} promise
     * @return {Promise}
     */
    function setGroupPromise(group, promise) {
        return StorageService.set([promisesPath, group], promise);
    }

    return _pub;
}

module.exports = CmsContentService;
